import { Fragment } from 'react';
import type { ContentBlock } from '#/domains/landing/types/content';
import type { RenderSection } from '#/domains/landing/schema/normalize';
import BenefitCard from './BenefitCard';
import Callout from './Callout';
import ContentTable from './ContentTable';
import TileGrid from './TileGrid';
import InlineLinks from './InlineLinks';

interface BodyContentProps {
  /** 정규화된 섹션들 — v1(bodyContent)·v2(sections) 모두 여기로 들어온다. */
  sections?: RenderSection[];
  /** 제목 없는 옛 `string[]` 본문. */
  looseParagraphs?: string[];
  /** 첫 섹션 바로 뒤에 끼워 넣을 노드 (목차의 `afterFirstSection` 배치). */
  afterFirstSection?: React.ReactNode;
  children?: React.ReactNode;
  contentBase?: string;
}

function FormattedText({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <>
      {lines.map((line, i) => (
        <span key={i}>
          {i > 0 && '\n'}
          <InlineLinks text={line} />
        </span>
      ))}
    </>
  );
}

const BULLET_PREFIX = '• ';

function isBulletList(text: string): boolean {
  return text.split('\n').every(line => line.startsWith(BULLET_PREFIX));
}

function BulletList({ text }: { text: string }) {
  const items = text.split('\n');
  return (
    <ul className="flex list-disc flex-col gap-2 pl-[1.15em]">
      {items.map((item, i) => (
        <li key={i} className="pl-[0.15em]">
          <InlineLinks text={item.slice(BULLET_PREFIX.length)} />
        </li>
      ))}
    </ul>
  );
}

function Paragraphs({ paragraphs }: { paragraphs: string[] }) {
  return (
    <div className="whitespace-pre-line text-base font-normal leading-[1.5] text-gray-80">
      {paragraphs.map((text, i) => {
        const spacing = i < paragraphs.length - 1 ? 'mb-4' : '';
        return isBulletList(text) ? (
          <div key={i} className={spacing}>
            <BulletList text={text} />
          </div>
        ) : (
          <p key={i} className={spacing}>
            <FormattedText text={text} />
          </p>
        );
      })}
    </div>
  );
}

/**
 * 블록 하나를 그린다. 감싸는 여백은 v1 렌더 결과와 픽셀 단위로 같게 유지한다
 * (문단 다음 콜아웃 `my-6`, 표 `mt-4`, 타일 `mt-2`).
 */
function Block({
  block,
  sectionAnchorId,
  contentBase,
}: {
  block: ContentBlock;
  sectionAnchorId: string;
  contentBase?: string;
}) {
  switch (block.type) {
    case 'paragraphs':
      return <Paragraphs paragraphs={block.items} />;
    case 'callout':
      return (
        <div className="my-6">
          <Callout title={block.title} description={block.description} />
        </div>
      );
    case 'benefitCards':
      return (
        <div className="-mx-6 flex flex-col gap-4 px-6">
          {block.items.map((card, k) => (
            <BenefitCard
              key={k}
              analyticsId={`benefit-card-section-${sectionAnchorId.replace(/^section-/, '')}-${card.serviceSeqNo ?? k}`}
              imageUrl={card.imageUrl}
              title={card.title}
              description={card.description}
              supportType={card.supportType}
              organization={card.organization}
              href={
                card.serviceSeqNo ? `/benefit/${card.serviceSeqNo}` : undefined
              }
              contentBase={contentBase}
            />
          ))}
        </div>
      );
    case 'table':
      return (
        <div className="mt-4">
          <ContentTable
            table={{
              headers: block.headers,
              rows: block.rows,
              caption: block.caption,
              groupFirstColumn: block.groupFirstColumn,
            }}
          />
        </div>
      );
    case 'tiles':
      return (
        <div className="mt-2">
          <TileGrid tiles={block.items} contentBase={contentBase} />
        </div>
      );
  }
}

function TitledSections({
  sections,
  contentBase,
  afterFirstSection,
}: {
  sections: RenderSection[];
  contentBase?: string;
  afterFirstSection?: React.ReactNode;
}) {
  return (
    <>
      {sections.map((section, sectionIndex) => (
        <Fragment key={section.anchorId}>
        <div
          id={section.anchorId}
          className="flex scroll-mt-16 flex-col gap-3"
        >
          <h3 className="-mx-6 line-clamp-2 w-screen max-w-[640px] whitespace-pre-line px-6 text-[20px] font-bold leading-[1.5] text-gray-100">
            <FormattedText text={section.title} />
          </h3>
          {section.blocks.map((block, blockIndex) => (
            <Block
              key={blockIndex}
              block={block}
              sectionAnchorId={section.anchorId}
              contentBase={contentBase}
            />
          ))}
        </div>
        {sectionIndex === 0 && afterFirstSection}
        </Fragment>
      ))}
    </>
  );
}

export default function BodyContent({
  sections,
  looseParagraphs,
  children,
  contentBase,
  afterFirstSection,
}: BodyContentProps) {
  const hasSections = sections != null && sections.length > 0;
  const hasLooseParagraphs = looseParagraphs != null && looseParagraphs.length > 0;

  return (
    <div className="flex w-full flex-col gap-8 px-6 pb-5">
      {hasSections && (
        <TitledSections
          sections={sections}
          contentBase={contentBase}
          afterFirstSection={afterFirstSection}
        />
      )}
      {hasLooseParagraphs && <Paragraphs paragraphs={looseParagraphs} />}
      {!hasSections && !hasLooseParagraphs && children}
    </div>
  );
}
